/**
 * Attribute-intent parsing for free-text vehicle search.
 *
 * Real queries look like "tacoma towing", "civic gas mileage", "f-150 tire
 * size" — a vehicle plus the data type the user actually wants. Without
 * parsing, the attribute word is treated as part of the vehicle name and the
 * search returns nothing even though the site has the exact page.
 *
 * Shared utility: any surface with a free-text vehicle box should route
 * through parseVehicleQuery so behaviour stays consistent.
 */

import { DATA_TYPES, isValidDataType, type DataTypeKey } from "@/lib/vehicle-data";

/**
 * Phrases that map onto a data type. Longer phrases must come first so
 * "gas mileage" wins over "gas" and "fuel tank" over "fuel".
 */
const ATTRIBUTE_PHRASES: { phrase: string; dataType: DataTypeKey }[] = [
  { phrase: "gas mileage", dataType: "mpg" },
  { phrase: "fuel economy", dataType: "mpg" },
  { phrase: "fuel efficiency", dataType: "mpg" },
  { phrase: "miles per gallon", dataType: "mpg" },
  { phrase: "mpg", dataType: "mpg" },

  { phrase: "annual fuel cost", dataType: "fuel-cost" },
  { phrase: "fuel cost", dataType: "fuel-cost" },
  { phrase: "gas cost", dataType: "fuel-cost" },
  { phrase: "cost to fill", dataType: "fuel-cost" },

  { phrase: "fuel tank capacity", dataType: "fuel-tank-capacity" as DataTypeKey },
  { phrase: "fuel tank size", dataType: "fuel-tank-capacity" as DataTypeKey },
  { phrase: "gas tank size", dataType: "fuel-tank-capacity" as DataTypeKey },
  { phrase: "gas tank", dataType: "fuel-tank-capacity" as DataTypeKey },
  { phrase: "tank capacity", dataType: "fuel-tank-capacity" as DataTypeKey },

  { phrase: "towing capacity", dataType: "towing-capacity" as DataTypeKey },
  { phrase: "tow rating", dataType: "towing-capacity" as DataTypeKey },
  { phrase: "tow capacity", dataType: "towing-capacity" as DataTypeKey },
  { phrase: "towing", dataType: "towing-capacity" as DataTypeKey },
  { phrase: "payload", dataType: "towing-capacity" as DataTypeKey },

  { phrase: "maintenance schedule", dataType: "maintenance-schedule" },
  { phrase: "service intervals", dataType: "maintenance-schedule" },
  { phrase: "service schedule", dataType: "maintenance-schedule" },
  { phrase: "oil change interval", dataType: "maintenance-schedule" },
  { phrase: "oil change", dataType: "maintenance-schedule" },
  { phrase: "maintenance", dataType: "maintenance-schedule" },

  { phrase: "safety ratings", dataType: "safety-ratings" as DataTypeKey },
  { phrase: "safety rating", dataType: "safety-ratings" as DataTypeKey },
  { phrase: "crash test", dataType: "safety-ratings" as DataTypeKey },
  { phrase: "nhtsa rating", dataType: "safety-ratings" as DataTypeKey },

  { phrase: "bolt pattern", dataType: "bolt-pattern" as DataTypeKey },
  { phrase: "lug pattern", dataType: "bolt-pattern" as DataTypeKey },
  { phrase: "wheel fitment", dataType: "bolt-pattern" as DataTypeKey },

  { phrase: "tire size", dataType: "tire-size" as DataTypeKey },
  { phrase: "tyre size", dataType: "tire-size" as DataTypeKey },
  { phrase: "tires", dataType: "tire-size" as DataTypeKey },

  { phrase: "bed dimensions", dataType: "bed-dimensions" as DataTypeKey },
  { phrase: "bed size", dataType: "bed-dimensions" as DataTypeKey },
  { phrase: "bed length", dataType: "bed-dimensions" as DataTypeKey },

  { phrase: "curb weight", dataType: "curb-weight" as DataTypeKey },
  { phrase: "weight", dataType: "curb-weight" as DataTypeKey },

  { phrase: "dimensions", dataType: "dimensions" as DataTypeKey },
  { phrase: "length", dataType: "dimensions" as DataTypeKey },
  { phrase: "width", dataType: "dimensions" as DataTypeKey },
  { phrase: "height", dataType: "dimensions" as DataTypeKey },
  { phrase: "wheelbase", dataType: "dimensions" as DataTypeKey },
  { phrase: "size", dataType: "dimensions" as DataTypeKey },

  { phrase: "electric range", dataType: "ev-range" as DataTypeKey },
  { phrase: "battery range", dataType: "ev-range" as DataTypeKey },
  { phrase: "charging time", dataType: "ev-range" as DataTypeKey },
  { phrase: "ev range", dataType: "ev-range" as DataTypeKey },
  { phrase: "range", dataType: "ev-range" as DataTypeKey },

  { phrase: "transmission", dataType: "transmission" },
  { phrase: "gearbox", dataType: "transmission" },

  { phrase: "drivetrain", dataType: "drivetrain" },
  { phrase: "drive type", dataType: "drivetrain" },
  { phrase: "awd", dataType: "drivetrain" },
  { phrase: "4wd", dataType: "drivetrain" },
  { phrase: "fwd", dataType: "drivetrain" },
  { phrase: "rwd", dataType: "drivetrain" },

  { phrase: "vehicle class", dataType: "vehicle-class" },
  { phrase: "body style", dataType: "vehicle-class" },

  { phrase: "engine specs", dataType: "engine" },
  { phrase: "horsepower", dataType: "engine" },
  { phrase: "displacement", dataType: "engine" },
  { phrase: "cylinders", dataType: "engine" },
  { phrase: "engine", dataType: "engine" },
];

export interface ParsedVehicleQuery {
  /** The query with the attribute phrase removed — the vehicle part. */
  vehicleQuery: string;
  /** Data type the query asked for, if any. */
  dataType: DataTypeKey | null;
  /** The literal words that triggered the match, for user-facing copy. */
  matchedPhrase: string | null;
  /** Four-digit model year found in the query, if any. */
  year: number | null;
}

const YEAR_RE = /\b(19[8-9]\d|20[0-4]\d)\b/;

/**
 * Vehicle names that contain an attribute word ("Range Rover" contains
 * "range"). These are masked before attribute matching so the model name
 * survives intact, then restored.
 */
const PROTECTED_NAME_PHRASES = ["range rover", "grand tourer", "town country"];

const MASK_PREFIX = "zzmask";

/** Replace protected model names with opaque tokens so attribute matching skips them. */
function maskNames(input: string): { text: string; masks: { token: string; phrase: string }[] } {
  let text = input;
  const masks: { token: string; phrase: string }[] = [];
  PROTECTED_NAME_PHRASES.forEach((phrase, i) => {
    const needle = ` ${phrase} `;
    if (text.includes(needle)) {
      const token = `${MASK_PREFIX}${i}`;
      text = text.split(needle).join(` ${token} `);
      masks.push({ token, phrase });
    }
  });
  return { text, masks };
}

function unmask(text: string, masks: { token: string; phrase: string }[]): string {
  let out = text;
  for (const { token, phrase } of masks) out = out.split(token).join(phrase);
  return out;
}

/**
 * Split a raw search string into vehicle terms plus an optional attribute
 * intent. Never throws; unmatched queries return the input unchanged.
 */
export function parseVehicleQuery(raw: string): ParsedVehicleQuery {
  const normalized = ` ${raw.toLowerCase().replace(/[^a-z0-9]+/g, " ").replace(/\s+/g, " ").trim()} `;
  if (normalized.trim() === "") {
    return { vehicleQuery: "", dataType: null, matchedPhrase: null, year: null };
  }

  const { text: masked, masks } = maskNames(normalized);
  let rest = masked;
  let dataType: DataTypeKey | null = null;
  let matchedPhrase: string | null = null;

  for (const { phrase, dataType: dt } of ATTRIBUTE_PHRASES) {
    const needle = ` ${phrase} `;
    if (rest.includes(needle)) {
      // Only strip the attribute when something else remains — a bare
      // "towing" query should still search normally rather than blank out.
      const stripped = rest.replace(needle, " ").replace(/\s+/g, " ");
      if (stripped.trim() === "") break;
      rest = stripped;
      dataType = isValidDataType(dt) ? dt : null;
      matchedPhrase = phrase;
      break;
    }
  }

  const yearMatch = YEAR_RE.exec(rest);
  const year = yearMatch ? Number(yearMatch[1]) : null;
  if (yearMatch) {
    const withoutYear = rest.replace(yearMatch[0], " ").replace(/\s+/g, " ").trim();
    // Keep the year in the vehicle query only if nothing else is left.
    rest = withoutYear === "" ? rest : ` ${withoutYear} `;
  }

  return {
    vehicleQuery: unmask(rest, masks).trim(),
    dataType,
    matchedPhrase,
    year,
  };
}

/** Human label for an intent, e.g. "towing capacity". */
export function intentLabel(dataType: DataTypeKey): string {
  return DATA_TYPES[dataType].shortLabel;
}
